import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { http } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // null = checking, false = guest, object = authenticated user
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await http.get("/auth/me", {
        validateStatus: (s) => s < 500,
      });
      if (data && data.id) setUser(data);
      else setUser(false);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
    // `http` is a stable module import; `setUser`/`setLoading` are stable React refs.
  }, []);  

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback(async (email, password) => {
    // Backend sets httpOnly access_token cookie on success.
    const { data } = await http.post("/auth/login", { email, password });
    setUser({ id: data.id, email: data.email, name: data.name, role: data.role });
    return data;
    // `http` + `setUser` are stable.
  }, []);  

  const logout = useCallback(async () => {
    try {
      await http.post("/auth/logout");
    } catch (err) {
      if (process.env.NODE_ENV !== "production") console.warn("[auth] logout request failed; clearing client state anyway", err);
    }
    setUser(false);
    // `http` + `setUser` are stable.
  }, []);  

  const value = useMemo(
    () => ({ user, loading, login, logout, refresh }),
    [user, loading, login, logout, refresh]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
